export type DiffSegment = { text: string; type: 'equal' | 'added' | 'removed' }

export const canonicalize = (str: string) => {
  return str
    .trim()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase()
}

/**
 * Computes a word-level diff between two strings using LCS.
 * Returns segments tagged as 'equal', 'added', or 'removed'.
 * Preserves whitespace between words for natural rendering.
 *
 * Tokens are compared in canonical form (accent/punctuation/case insensitive)
 * while the original text is preserved in the output segments.
 *
 * - 'removed' segments exist only in `a` (old/left)
 * - 'added' segments exist only in `b` (new/right)
 */
export const computeDiff = (a: string, b: string): DiffSegment[] => {
  const tokenize = (str: string): string[] => str.split(/(\s+)/).filter((t) => t.length > 0)

  const wordsA = tokenize(a)
  const wordsB = tokenize(b)
  const canonA = wordsA.map(canonicalize)
  const canonB = wordsB.map(canonicalize)
  const m = wordsA.length
  const n = wordsB.length

  const matches = (i: number, j: number) => {
    const ca = canonA[i] ?? ''
    const cb = canonB[j] ?? ''
    // Whitespace tokens canonicalize to '' — match them to each other, not to
    // empty-canonical word tokens like "-" or ".".
    const aIsWs = /^\s+$/.test(wordsA[i] ?? '')
    const bIsWs = /^\s+$/.test(wordsB[j] ?? '')
    if (aIsWs !== bIsWs) return false
    if (aIsWs && bIsWs) return true
    return ca.length > 0 && ca === cb
  }

  // Build LCS table
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array<number>(n + 1).fill(0))
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const row = dp[i]
      if (!row) continue
      row[j] = matches(i - 1, j - 1)
        ? (dp[i - 1]?.[j - 1] ?? 0) + 1
        : Math.max(dp[i - 1]?.[j] ?? 0, row[j - 1] ?? 0)
    }
  }

  // Backtrack to get diff operations
  const ops: DiffSegment[] = []
  let i = m
  let j = n
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && matches(i - 1, j - 1)) {
      ops.push({ text: wordsA[i - 1] ?? '', type: 'equal' })
      i--
      j--
    } else if (j > 0 && (i === 0 || (dp[i]?.[j - 1] ?? 0) >= (dp[i - 1]?.[j] ?? 0))) {
      ops.push({ text: wordsB[j - 1] ?? '', type: 'added' })
      j--
    } else {
      ops.push({ text: wordsA[i - 1] ?? '', type: 'removed' })
      i--
    }
  }
  ops.reverse()

  // Merge consecutive segments of the same type
  const merged: DiffSegment[] = []
  for (const op of ops) {
    const last = merged[merged.length - 1]
    if (last && last.type === op.type) {
      last.text += op.text
    } else {
      merged.push({ ...op })
    }
  }

  return merged
}
